// src/components/Hero.js
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { gsap } from "gsap";
import cardDataObject from "./card-data";

/* ================================
   Animation & Layout Constants
================================ */
const GAP_PX = 4;
const PER_CARD_ROTATE_BASE = -5;
const PER_CARD_JITTER = 10;

const ZOOM_SCALE = 1.25;
const PULL_UP_DUR = 0.85;
const PULL_HOLD = 1.6;
const PULL_DOWN_DUR = 0.85;
const PAUSE_BETWEEN = 2.0;

const IDLE_Y_MIN = 6, IDLE_Y_MAX = 14;
const IDLE_X_MIN = -5, IDLE_X_MAX = 5;
const IDLE_ROT = 2.0;

const GRID_INSET = { top: 2, right: 2, bottom: 2, left: 2 };

/* ================================
   Data helpers
================================ */
const allCards = Object.values(cardDataObject);

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = (Math.random() * (i + 1)) | 0;
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ================================
   Responsive image helpers
   - Default: Imgix-like params (?auto=format,compress&fit=crop&w=...&h=...&dpr=...&q=...)
   - For ImageKit:  return `${src}?tr=w-${w},h-${h},fo-auto,q-${q}`
   - For Cloudinary: return src.replace("/upload/", `/upload/c_fill,f_auto,q_${q},w_${w},h_${h}/`)
   - If you pre-generate sizes, just map to your files here instead.
================================ */
function buildUrl(src, { w, h, dpr = 1, q = 60 }) {
  const W = Math.round(w * dpr);
  const H = Math.round(h * dpr);
  // IMGIX / many CDNs (adjust if needed)
  return `${src}?auto=format,compress&fit=crop&w=${W}&h=${H}&dpr=${dpr}&q=${q}`;
}

export default function Hero() {
  // DOM refs
  const root = useRef(null);
  const gridRef = useRef(null);
  const idleTweensRef = useRef([]);
  const delayedRef = useRef(null);
  const captionRef = useRef(null);
  const sweepRef = useRef(null);

  // Layout state — always exactly 2 rows
  const [grid, setGrid] = useState({ cols: 10, rows: 2, cardW: 100, cardH: 150 });

  // Card count: 10 on small (<650), 20 on large (≥650)
  const initialCount = typeof window !== "undefined" && window.innerWidth < 650 ? 10 : 20;
  const [numCards, setNumCards] = useState(initialCount);

  // Fixed 1px inset
  const [inset] = useState(GRID_INSET);

  // Stable deck (re-sliced when numCards changes)
  const baseDeck = useMemo(() => shuffle(allCards), []);
  const deckN = useMemo(() => baseDeck.slice(0, numCards), [baseDeck, numCards]);

  // Switch count + compute exact 2-row layout
  useEffect(() => {
    const calc = () => {
      if (!root.current) return;

      const targetCount = window.innerWidth < 650 ? 10 : 20;
      if (targetCount !== numCards) setNumCards(targetCount);

      const r = root.current.getBoundingClientRect();
      const availW = Math.max(0, r.width - inset.left - inset.right);
      const availH = Math.max(0, r.height - inset.top - inset.bottom);

      const rows = 2;
      const cols = Math.ceil(targetCount / rows); // 5/10 for 10/20

      const wFromWidth  = (availW - (cols - 1) * GAP_PX) / cols;
      const wFromHeight = (availH - (rows - 1) * GAP_PX) / (1.5 * rows);
      const cardW = Math.max(28, Math.min(wFromWidth, wFromHeight));
      const cardH = Math.round(cardW * 1.5);

      setGrid({ cols, rows, cardW: Math.round(cardW), cardH });
    };

    let raf = null;
    const onResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(calc);
    };

    calc();
    window.addEventListener("resize", onResize);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [inset, numCards]);

  // Animations (idle + spotlight zoom)
  useLayoutEffect(() => {
    if (!gridRef.current) return;
    const cards = gsap.utils.toArray(".diag-card");
    if (!cards.length) return;

    gsap.fromTo(gridRef.current, { opacity: 0 }, { opacity: 1, duration: 0.6, ease: "power1.out" });

    // Reset previous tweens / timers
    idleTweensRef.current.forEach((t) => t.kill());
    idleTweensRef.current = [];
    if (delayedRef.current) {
      delayedRef.current.kill?.();
      delayedRef.current = null;
    }

    // Base scattered rotation
    cards.forEach((el) => {
      const base = PER_CARD_ROTATE_BASE + gsap.utils.random(-PER_CARD_JITTER, PER_CARD_JITTER);
      gsap.set(el, { rotate: base, transformOrigin: "50% 50%" });
    });

    // Gentle idle float + slight rotation (no scaling here)
    cards.forEach((el) => {
      idleTweensRef.current.push(
        gsap.to(el, {
          y: `+=${gsap.utils.random(IDLE_Y_MIN, IDLE_Y_MAX)}`,
          x: `+=${gsap.utils.random(IDLE_X_MIN, IDLE_X_MAX)}`,
          duration: gsap.utils.random(3.0, 5.0),
          ease: "sine.inOut",
          yoyo: true,
          repeat: -1,
          delay: gsap.utils.random(0, 0.8),
        })
      );
      idleTweensRef.current.push(
        gsap.to(el, {
          rotate: `+=${gsap.utils.random(-IDLE_ROT, IDLE_ROT)}`,
          duration: gsap.utils.random(4.0, 6.0),
          ease: "sine.inOut",
          yoyo: true,
          repeat: -1,
          delay: gsap.utils.random(0, 0.8),
        })
      );
    });

    // Spotlight zoom (ONE card at a time)
    const zoomOnce = (el, next) => {
      const tl = gsap.timeline({
        onStart: () => gsap.set(el, { zIndex: 10 }),
        onComplete: () => {
          gsap.set(el, { zIndex: 1 });
          delayedRef.current = gsap.delayedCall(PAUSE_BETWEEN, next);
        },
      });
      tl.to(el, { scale: ZOOM_SCALE, duration: PULL_UP_DUR, ease: "power2.out" })
        .to(el, { scale: 1, duration: PULL_DOWN_DUR, ease: "power2.out" }, `+=${PULL_HOLD}`);
    };

    const runSequence = () => {
      if (!cards.length) return;
      const el = cards[(Math.random() * cards.length) | 0];
      zoomOnce(el, runSequence);
    };

    delayedRef.current = gsap.delayedCall(0.6, runSequence);

    return () => {
      idleTweensRef.current.forEach((t) => t.kill());
      if (delayedRef.current) delayedRef.current.kill();
      gsap.killTweensOf(cards);
    };
  }, [grid]);

  return (
    <section
      ref={root}
      className="relative z-[50] w-full overflow-x-clip mx-auto flex flex-col justify-end
                 max-w-[1150px] min-[950px]:max-w-[1200px]
                 backdrop-blur-none bg-[white]/10 border border-white/50 rounded-md
                 shadow-[0_1px_5px_0_rgba(0,0,0,0.8)]
                 px-4 py-2 min-h-[250px] min-[950px]:min-h-[350px]"
    >
      {/* Card Grid */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <div
          ref={gridRef}
          style={{
            top: inset.top,
            right: inset.right,
            bottom: inset.bottom,
            left: inset.left,
            position: "absolute",
            display: "grid",
            gridTemplateColumns: `repeat(${grid.cols}, ${grid.cardW}px)`,
            gridAutoRows: `${grid.cardH}px`,
            gap: `${GAP_PX}px`,
            justifyContent: "center",
            alignContent: "start",
            opacity: 0,
            willChange: "opacity, transform",
          }}
        >
          {deckN.map((c, i) => {
            const w = grid.cardW;
            const h = grid.cardH;

            // Build right-sized URLs for 1x & 2x DPR
            const src1x = buildUrl(c.image, { w, h, dpr: 1, q: 60 });
            const src2x = buildUrl(c.image, { w, h, dpr: 2, q: 60 });

            return (
              <div key={`${c.slug || "card"}-${i}`} className="diag-card">
                <img
                  src={src1x}
                  srcSet={`${src1x} 1x, ${src2x} 2x`}
                  sizes={`${Math.ceil(w)}px`}
                  alt={c.name || "Tarot card"}
                  width={w}
                  height={h}
                  draggable={false}
                  decoding="async"
                  className="block w-full h-full object-cover rounded-[3px] shadow-[0_4px_10px_rgba(0,0,0,0.25)]"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Legibility gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-[067399]/0 via-black/0 to-transparent z-[1]" />

      {/* Bottom caption */}
      <div
        className="
          absolute left-1/2 bottom-2 -translate-x-1/2 translate-y-[0%]
          z-[2]
          w-[100%] sm:w-[100%] md:w-[100%] lg:w-[1200px] max-w-[100vw]
          select-none
        "
        ref={captionRef}
      >
        <div
          className="
            absolute inset-x-[-8px] -inset-y-1
            rounded-t-none rounded-b-[16px]
            bg-gradient-to-t from-[#8F0131]/80 via-[#021868]/60 to-[#14656a]/50
            blur-[10px] pointer-events-none
          "
        />
        <div className="relative text-white text-center px-3 sm:px-4 md:px-5 py-2 sm:py-2 md:py-3">
          <p className="mb-0 text-[clamp(18px,3.2vw,24px)] font-semibold leading-[1.2] drop-shadow-sm font-[Cormorant_Garamond]">
            In this sacred stillness, the heart speaks in symbols and silence
          </p>
          <div
            className="
              w-[88%] mx-auto my-1 h-[2px] rounded-full
              bg-[radial-gradient(circle,_#FCF004_12%,_transparent_100%)]
              animate-pulse
            "
          />
          <p className="text-[clamp(17px,2.8vw,20px)] leading-[1.1] drop-shadow-sm font-[Cormorant_Garamond]">
            Here, your emotion leads — the tarot cards reflect it.
          </p>
        </div>
      </div>
    </section>
  );
}
